import React, { useState } from 'react'
import api from '../../lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { Eye, EyeOff, Mail, User, Lock, Check, Shield, CreditCard, Zap } from 'lucide-react'

interface CreateAccountStepProps {
  onComplete: (data: { email: string; name: string; requiresEmailVerification?: boolean }) => void
  initialData?: {
    email: string
    name: string
  }
}

export default function CreateAccountStep({ onComplete, initialData }: CreateAccountStepProps) {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    password: '',
    confirmPassword: ''
  })
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const { toast } = useToast()

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter'
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter'
    } else if (!/\d/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number'
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    if (!acceptedTerms) {
      newErrors.terms = 'Please agree to the Terms of Service and Privacy Policy to continue'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      // Pass through any access code stashed by /redeem so the backend
      // attaches it to the new agent server-side. From this point on the
      // code lives on the agent row, not in client storage — survives
      // email verification on a different device or browser.
      const accessCode = localStorage.getItem('app_access_code') || undefined
      const affiliateMeta = (() => {
        try {
          const raw = localStorage.getItem('legallady_affiliate_ref_meta')
          return raw ? JSON.parse(raw) : null
        } catch {
          return null
        }
      })()
      const affiliateExpired = affiliateMeta?.expiresAt && new Date(affiliateMeta.expiresAt).getTime() < Date.now()
      const affiliateCode = affiliateExpired
        ? undefined
        : localStorage.getItem('legallady_affiliate_ref') || undefined

      const response = await api.post('/api/onboarding/account', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        ...(accessCode ? { accessCode } : {}),
        ...(affiliateCode ? { affiliateCode } : {}),
      })

      if (response.data.success) {
        // Store token for subsequent onboarding steps
        if (response.data.token) {
          localStorage.setItem('app_auth_token', response.data.token)
          // Set auth header for subsequent API calls
          api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`
        }

        // Store agent data if provided
        if (response.data.agent) {
          localStorage.setItem('app_agent_data', JSON.stringify(response.data.agent))
        }

        const needsVerification = !!response.data.requiresEmailVerification
        toast({
          title: 'Account created',
          description: needsVerification
            ? 'Check your email to verify your address.'
            : 'Your account has been created successfully'
        })

        onComplete({
          email: formData.email,
          name: formData.name,
          requiresEmailVerification: needsVerification
        })
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to create account'
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive'
      })

      if (message.toLowerCase().includes('email')) {
        setErrors({ email: message })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const passwordsMatch = formData.password.length > 0 && formData.password === formData.confirmPassword
  const emailValid = formData.email.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
  const passwordValid =
    formData.password.length >= 8 &&
    /[A-Z]/.test(formData.password) &&
    /[a-z]/.test(formData.password) &&
    /\d/.test(formData.password)

  const trustItems = [
    { icon: Zap, label: 'Live in minutes', detail: 'Pick a plan and your AI receptionist is ready before you finish onboarding' },
    { icon: CreditCard, label: 'Month-to-month', detail: 'No long-term contract — cancel anytime from your Billing page' },
    { icon: Shield, label: 'No setup fees', detail: 'You only pay your monthly plan, nothing else' },
  ]

  return (
    <div className="w-full">
      {/* 2-column split on lg+: heading/trust on left, form on right.
          Collapses to single column on mobile. Keeps everything above
          the fold on typical desktop viewports. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-8 lg:gap-12 items-start">
        {/* LEFT — intro + trust pitch */}
        <div className="text-center lg:text-left">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-periwinkle/80 mb-3">
            Step 01 &mdash; Account
          </p>
          <h2 className="text-2xl sm:text-3xl lg:text-[2rem] font-heading font-semibold text-white tracking-tight leading-[1.1]">
            Create your account.
          </h2>
          <p className="text-white/60 mt-3 text-sm leading-relaxed max-w-sm mx-auto lg:mx-0">
            Takes a minute. You'll be answering calls 24/7 before the hour is up.
          </p>

          <ul className="hidden lg:block mt-8 space-y-3">
            {trustItems.map((item) => (
              <li key={item.label} className="flex items-start gap-3">
                <div className="h-7 w-7 rounded-lg bg-brand-royal/20 border border-brand-royal/30 flex items-center justify-center shrink-0 mt-0.5">
                  <item.icon className="h-3 w-3 text-brand-periwinkle" strokeWidth={2} />
                </div>
                <div>
                  <p className="text-sm font-medium text-white leading-tight">{item.label}</p>
                  <p className="text-[11px] text-white/45 mt-0.5 leading-snug">{item.detail}</p>
                </div>
              </li>
            ))}
          </ul>

          <p className="hidden lg:block text-sm text-white/55 mt-8 pt-6 border-t border-white/10">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => {
                localStorage.removeItem('app_auth_token')
                localStorage.removeItem('app_onboarding_complete')
                window.location.href = '/login'
              }}
              className="text-brand-periwinkle hover:text-white font-medium transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
            >
              Sign in
            </button>
          </p>
        </div>

        {/* RIGHT — form fields stacked single-column in this half */}
        <form onSubmit={handleSubmit}>
          <fieldset disabled={isSubmitting} className="border-0 p-0 m-0 min-w-0 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Your Full Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`pl-10 pr-10 h-11 rounded-xl ${errors.name ? 'border-destructive' : ''}`}
                />
                {formData.name.trim().length > 0 && !errors.name && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-400 animate-in fade-in zoom-in-75 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]" strokeWidth={2.5} />
                )}
              </div>
              {errors.name && <p className="text-xs text-destructive animate-in fade-in slide-in-from-top-1 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]">{errors.name}</p>}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@lawfirm.com"
                  autoComplete="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`pl-10 pr-10 h-11 rounded-xl ${errors.email ? 'border-destructive' : ''}`}
                />
                {emailValid && !errors.email && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-400 animate-in fade-in zoom-in-75 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]" strokeWidth={2.5} />
                )}
              </div>
              {errors.email && <p className="text-xs text-destructive animate-in fade-in slide-in-from-top-1 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]">{errors.email}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a password"
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`pl-10 pr-12 h-11 rounded-xl ${errors.password ? 'border-destructive' : ''}`}
                  />
                  {passwordValid && !errors.password && (
                    <Check className="absolute right-9 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-400 animate-in fade-in zoom-in-75 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]" strokeWidth={2.5} />
                  )}
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-1 top-1/2 -translate-y-1/2 p-2 rounded text-muted-foreground hover:text-foreground transition-colors duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-90"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-xs text-destructive animate-in fade-in slide-in-from-top-1 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]">{errors.password}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm password"
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className={`pl-10 pr-10 h-11 rounded-xl ${errors.confirmPassword ? 'border-destructive' : ''}`}
                  />
                  {passwordsMatch && !errors.confirmPassword && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-400 animate-in fade-in zoom-in-75 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]" strokeWidth={2.5} />
                  )}
                </div>
                {errors.confirmPassword && (
                  <p className="text-xs text-destructive animate-in fade-in slide-in-from-top-1 duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            {!errors.password && (
              <p className="text-[11px] text-white/40 -mt-1.5">Min 8 chars · uppercase · lowercase · number</p>
            )}

            <label className="flex items-start gap-3 cursor-pointer group pt-1">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => {
                  setAcceptedTerms(e.target.checked)
                  if (errors.terms) setErrors({ ...errors, terms: '' })
                }}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/30 bg-white/5 text-brand-royal focus:ring-2 focus:ring-brand-royal focus:ring-offset-0 cursor-pointer"
              />
              <span className="text-xs text-white/70 leading-relaxed select-none">
                I agree to the{' '}
                <a href="/terms" target="_blank" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" target="_blank" className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
                  Privacy Policy
                </a>
              </span>
            </label>
            {errors.terms && (
              <p className="text-xs text-destructive">{errors.terms}</p>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-11 rounded-xl text-[15px] bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 text-white font-semibold shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] border-0 disabled:active:scale-100"
            >
              {isSubmitting ? 'Creating account…' : 'Create account'}
            </Button>

            {/* Mobile sign-in link (desktop version lives in the left column) */}
            <p className="lg:hidden text-sm text-center text-white/55 pt-3 border-t border-white/10">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem('app_auth_token')
                  localStorage.removeItem('app_onboarding_complete')
                  window.location.href = '/login'
                }}
                className="text-brand-periwinkle hover:text-white font-medium"
              >
                Sign in
              </button>
            </p>
          </fieldset>
        </form>
      </div>
    </div>
  )
}
